import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form3921Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 3921 instructions for Tax Year 2025"
          description="Learn step-by-step instructions for IRS Form 3921 for Tax Year 2025. Report incentive stock option exercises accurately with guidance from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-3921-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 3921</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Simplify your stock option filing—fast, accurate, and stress-free!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3921instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-3921-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 3921 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 3921, Exercise of an Incentive Stock Option Under
                          Section 422(b)
                        </strong>
                        {
                          ', is used by corporations to report the transfer of shares '
                        }
                        acquired
                        {
                          ' through the exercise of an incentive stock option (ISO) under Section 422 of the Internal Revenue Code'
                        }
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {', the IRS filing deadline is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        {
                          'This article covers line-by-line instructions, including corporation and employee information, '
                        }
                        <strong style={{ fontWeight: 600 }}>{'Box 1 '}</strong>
                        (Date Option Granted)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Date Option Exercised)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Exercise Price per Share)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Fair Market Value per Share on Exercise Date)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 5 '}</strong>
                        (Number of Shares Transferred)
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        (Date Form Filed) , along with other reporting
                        requirements for Form 3921.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or incomplete filing of Form 3921 may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on when the return is filed, whether
                        corrections are required, and whether the filer complies
                        with electronic filing requirements.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  Form 3921 is an IRS information return used to report stock
                  transfers resulting from the exercise of an Incentive Stock
                  Option (ISO). It helps taxpayers track potential tax
                  implications related to ISOs, making it a crucial stock option
                  to return for both corporations and employees.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to file Form 3921?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key requirements for filing Form 3921
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#dead" sx={{ color: '#0202ff' }}>
                          Important deadline for Form 3921
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" id="who" sx={{ color: '#0202ff' }}>
                          Form 3921 instructions on how to fill out Form 3921
                          online?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 3921?
                </Typography>
                <Typography variant="body1" id="key" sx={{ mb: 3 }}>
                  Corporations are required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 3921
                  </Link>{' '}
                  if they transfer shares of stock to any person during the
                  calendar year as a result of that person exercising an
                  Incentive Stock Option (ISO), which is in Section 422(b) of
                  the Internal Revenue Code.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key requirements for filing Form 3921
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 3921 can be filed either online through e-filing or by
                  sending paper forms directly to the IRS. Before filing, you
                  must know the information needed to file.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Employee information for those who exercised the Incentive
                  Stock Option, including their name, address, and TIN.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Date the incentive stock option was granted </li>
                  <li>Date the option was exercised </li>
                  <li>Exercise price per share </li>
                  <li>
                    Fair market value of each share on the date the option was
                    exercised.{' '}
                  </li>
                  <li>Number of shares transferred </li>
                </Box>
                <Alert
                  severity="warning"
                  id="dead"
                  icon={false}
                  sx={{ mt: 2, mb: 4 }}
                >
                  <b>Note:</b> If you are paper filing more than 10 forms, then
                  you have to file{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 3921 online
                  </Link>
                  .
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                  }}
                >
                  Important deadline for Form 3921
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your company transfers stock to an employee through an
                  incentive stock option (ISO) plan, you&apos;re required to
                  file <span style={{ fontWeight: 600 }}> Form 3921</span> with
                  the IRS and furnish a copy to the employee.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For the <span style={{ fontWeight: 600 }}>2025 tax year</span>
                  , the{' '}
                  <Link
                    href="/resources/information-returns/form-3921-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 3921 deadline
                  </Link>{' '}
                  is as follows:{' '}
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing Type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Sending employee copy
                          </TableCell>

                          <TableCell sx={{}}>February 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            IRS e-filing
                          </TableCell>

                          <TableCell sx={{}} id="form">
                            March 31, 2026{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                  }}
                >
                  Form 3921 instructions on how to fill out form 3921 online ?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Let’s see the step-by-step process of fill out Form 3921
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Transferor’s details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-transferor-img.webp" />
                  <source srcSet="/assets/images/f3921-transferor-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-transferor-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Transferor’s details"
                    width={459}
                    height={181}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name, address (street, city, state, ZIP), and the
                  Taxpayer Identification Number (TIN) of the entity
                  transferring the stock.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Employee’s details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/f3921-employee-img.webp" />
                  <source srcSet="/assets/images/f3921-employee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-employee-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Employee’s details"
                    width={459}
                    height={203}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the employee’s name, address, and Taxpayer
                  Identification Number (TIN) to whom the shares were
                  transferred as a result of exercising the option.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Stock Options & Exercise Details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Account Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-account-img.webp" />
                  <source srcSet="/assets/images/f3921-account-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-account-img.png"
                    loading="lazy"
                    alt=" Account Number"
                    width={459}
                    height={47}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The account number is required to identify and track multiple
                  Form 3921 filings for the same person. If an individual has
                  more than one account or transaction related to the exercise
                  of an incentive stock option (ISO), the account number allows
                  the IRS and the filer to distinguish between those separate
                  filings.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Date Option Granted
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f3921-date-option-img.webp" />
                    <source srcSet="/assets/images/f3921-date-option-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f3921-date-option-img.png"
                      loading="lazy"
                      alt="Box 1: Date Option Granted"
                      width={190}
                      height={69}
                      className="img-responsive"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the precise date on which the corporation formally
                  granted the Incentive Stock Option (ISO) to the employee.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Date Option Exercised
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-date-exercised-img.webp" />
                  <source srcSet="/assets/images/f3921-date-exercised-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-date-exercised-img.png"
                    loading="lazy"
                    alt="Box 2: Date Option Exercised"
                    width={190}
                    height={70}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date on which the employee exercised the stock
                  option.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Exercise Price Per Share
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-price-share-img.webp" />
                  <source srcSet="/assets/images/f3921-price-share-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-price-share-img.png"
                    loading="lazy"
                    alt="Box 3: Exercise Price Per Share"
                    width={190}
                    height={69}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The corporation is required to enter the exercise price for
                  each share of stock.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Fair Market Value per share on exercise date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-fair-market-img.webp" />
                  <source srcSet="/assets/images/f3921-fair-market-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-fair-market-img.png"
                    loading="lazy"
                    alt="Box 4: Fair Market Value per share on exercise date"
                    width={190}
                    height={114}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the fair market value (FMV) per share of the company’s
                  stock on the date the employee exercised the option.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Number of Shares Transferred
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-number-share-img.webp" />
                  <source srcSet="/assets/images/f3921-number-share-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-number-share-img.png"
                    loading="lazy"
                    alt="Box 5: Number of Shares Transferred "
                    width={190}
                    height={46}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total number of shares that were transferred to the
                  employee when the stock option was exercised.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: If Other Than TRANSFEROR, Name, Address, and TIN of
                  Corporation Whose Stock Is Being Transferred
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3921-corporation-img.webp" />
                  <source srcSet="/assets/images/f3921-corporation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3921-corporation-img.png"
                    loading="lazy"
                    alt="Box 6: If Other Than TRANSFEROR, Name, Address, and TIN"
                    width={378}
                    height={136}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name, address, and TIN of the corporation
                  transferring the stock as part of the option to exercise. Only
                  provide this information if it is different from the
                  corporation listed in the{' '}
                  <span style={{ fontWeight: 600 }}> TRANSFEROR</span> section
                  at the top left corner of this{' '}
                  <span style={{ fontWeight: 600 }}>Form</span>.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given is based on the IRS guidelines.
                        For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i3921.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          instructions of Form 3921
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          General instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Easily file Form 3921 with TaxZerone!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Get ready to file forms?
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3921instructions`}
              >
                E-File Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form3921Instructions;
